import { useMemo } from 'react'

import { useQuery } from '@apollo/client'
import {
  JobCatalogEnumsJobCatalogTreeNodeTypesEnum,
  NodeAttributesFragment,
  NodeCompetencyAttributesFragment,
  NodePageDocument,
} from 'generated/resources/graphql'

import {
  EquipmentDisplayItem,
  getEquipmentForDisplay,
} from 'modules/job_catalog/lib/equipmentUtils'
import {
  DEFAULT_CATEGORIES,
  NODE_UUID_TYPES,
  TreeNode,
  findNodeByUuid,
  formatUuidToNodeType,
  getAllSalariesFromChildrenAttributes,
  getChildNodeType,
  getNodeId,
  getNodeType,
  getSalariesFromAttributes,
} from 'modules/job_catalog/lib/nodesUtils'
import { Salary } from 'modules/job_catalog/lib/salary'
import {
  WorkingConditions,
  getParentsWorkingConditionsFromAttributes,
  getWorkingConditionsFromAttributes,
} from 'modules/job_catalog/lib/workingConditions'

interface UseTreeNode {
  nodeId: number
  nodeName?: string
  nodeType: string
  currentAttributes: NodeAttributesFragment[]
  competencyAttributes: NodeCompetencyAttributesFragment[]
  currentNode?: TreeNode | null
  salaries: Salary[]
  workingConditions?: WorkingConditions
  workingConditionsFromParentNode?: WorkingConditions
  childrenInformation: ChildInformation[]
  equipmentDisplayItems: EquipmentDisplayItem[]
  loading: boolean
}

interface ChildInformation {
  id: number
  name: string
  type: JobCatalogEnumsJobCatalogTreeNodeTypesEnum
  attributesJson: string
}

export const useTreeNode = (id: string | undefined): UseTreeNode => {
  const nodeId = getNodeId(id)
  const nodeType = getNodeType(id)
  const isLevel = useMemo(() => nodeType === NODE_UUID_TYPES.LEVEL, [nodeType])
  const isValidNode = nodeId && nodeType && !isNaN(nodeId)
  const childrenType = getChildNodeType(nodeType) || ''

  const { data, loading } = useQuery(NodePageDocument, {
    variables: {
      childType: formatUuidToNodeType(childrenType),
      nodeUuid: id ?? '',
    },
    skip: !isValidNode,
  })

  const nodeAttributes = useMemo(
    () => data?.jobCatalog?.nodeAttributesConnection?.nodes ?? [],
    [data?.jobCatalog?.nodeAttributesConnection?.nodes]
  )
  const currentAttributes = useMemo(
    () => nodeAttributes.filter((node) => node.takesPrecedence) || [],
    [nodeAttributes]
  )
  const parentAttributes = useMemo(
    () => nodeAttributes.filter((node) => !node.takesPrecedence) || [],
    [nodeAttributes]
  )

  const childrenInformation: ChildInformation[] = useMemo(() => {
    const rawChildren = data?.jobCatalog?.treeNodesConnection?.nodes || []
    return rawChildren.map((child) => ({
      id: child.id,
      name: child.name || '',
      type: child.type || '',
      attributesJson: child.attributesJson || '[]',
    }))
  }, [data?.jobCatalog?.treeNodesConnection?.nodes])

  const treeArray: TreeNode[] = useMemo(() => {
    try {
      const tree = data?.jobCatalog?.treesConnection?.nodes?.[0]
      if (!tree?.serializedTree) return []

      const parsed = JSON.parse(tree.serializedTree)
      return Array.isArray(parsed) ? parsed : [parsed]
    } catch (e) {
      return []
    }
  }, [data?.jobCatalog?.treesConnection?.nodes])

  const attachParents = (node: TreeNode, parent?: TreeNode): void => {
    node.parent = parent
    node.children?.forEach((child) => attachParents(child, node))
  }
  treeArray.forEach((root) => attachParents(root))

  const currentNode = useMemo(() => {
    if (!id || !treeArray.length) return undefined
    return findNodeByUuid(treeArray, id)
  }, [treeArray, id])

  const nodeName = currentNode?.name

  const salaries = useMemo(() => {
    if (isLevel) return getSalariesFromAttributes(currentAttributes)
    else return getAllSalariesFromChildrenAttributes(childrenInformation)
  }, [currentAttributes, childrenInformation, isLevel])

  const workingConditions = useMemo(
    () => getWorkingConditionsFromAttributes(currentAttributes, nodeType),
    [currentAttributes, nodeType]
  )

  const workingConditionsFromParentNode = useMemo(
    () => getParentsWorkingConditionsFromAttributes(parentAttributes, nodeType),
    [parentAttributes, nodeType]
  )

  const competencyAttributes: NodeCompetencyAttributesFragment[] = useMemo(
    () => currentAttributes.filter((node) => node.type === DEFAULT_CATEGORIES.COMPETENCIES),
    [currentAttributes]
  )

  const equipmentDisplayItems = useMemo(
    () => getEquipmentForDisplay(currentAttributes, nodeType),
    [currentAttributes, nodeType]
  )

  return {
    nodeId,
    nodeName,
    nodeType,
    currentNode,
    currentAttributes,
    competencyAttributes,
    childrenInformation,
    salaries,
    workingConditions,
    workingConditionsFromParentNode,
    equipmentDisplayItems,
    loading,
  }
}
